// ── Terms of Service / Privacy Policy — content only, kept separate from the
// Settings.tsx panels that render it. This is a STARTING DRAFT written to
// accurately describe how this app is actually built (per-tenant data
// isolation, what's stored, how it's used) — it is not legal advice, and
// should be reviewed by a lawyer familiar with Belgian/EU law (GDPR applies)
// before you publish it to customers.

export interface LegalSection {
  heading: string;
  body: string[];
}

const COMPANY = 'Matrix Developer';
// Exported so Settings.tsx's renderer can bold every occurrence of it in
// the paragraph text, rather than hand-marking each one in the content.
export const SUPPORT_EMAIL = 'info@diawolt.com';
const JURISDICTION = 'Belgium';
const LAST_UPDATED = '9 October 2026';
// Terms changed on their own (the yearly fee clause in section 4).
const TERMS_UPDATED = '16 October 2026';

export const TERMS_LAST_UPDATED = TERMS_UPDATED;
export const PRIVACY_LAST_UPDATED = LAST_UPDATED;

export const TERMS_SECTIONS: LegalSection[] = [
  {
    heading: '1. Acceptance of Terms',
    body: [
      `These Terms of Service ("Terms") govern your access to and use of this application (the "Service"), provided by ${COMPANY}. By creating an account or using the Service, you agree to be bound by these Terms. If you're using the Service on behalf of a business, you're confirming you have the authority to bind that business to these Terms.`,
    ],
  },
  {
    heading: '2. What the Service Does',
    body: [
      'The Service is a business-management application for diamond and jewelry trading businesses — inventory tracking, purchase and sales invoicing, consignment (memo) tracking, accounts payable/receivable, bank reconciliation, and related accounting records. You are responsible for the accuracy of the data you enter, and for using it to run your own business — the Service is a record-keeping and workflow tool, not a source of legal, tax, or accounting advice.',
    ],
  },
  {
    heading: '3. Accounts & Access',
    body: [
      'Each business using the Service has one Owner account and, optionally, additional Staff accounts the Owner creates and grants specific page access to. You are responsible for keeping your login credentials confidential and for all activity under your account. Notify us immediately at ' + SUPPORT_EMAIL + ' if you suspect unauthorized access.',
    ],
  },
  {
    heading: '4. Subscription & Fees',
    body: [
      'Access to the Service is provided on a subscription basis, as agreed at signup or renewal. Fees, billing cycles, and any per-additional-user charges are as communicated to you at the time of purchase or renewal. Subscriptions do not renew automatically without notice; an administrator will reach out ahead of your renewal date. Fees already paid are non-refundable except where required by law.',
      'We may increase the annual subscription fee. If we do, we will tell you in writing at least 3 months before the new fee applies, and it applies from your first renewal after that notice period. The fee for a year you have already paid never changes. If you do not accept the new fee, you may choose not to renew, and you keep full access until the end of the period you have paid for.',
    ],
  },
  {
    heading: '5. Your Data',
    body: [
      'You own the business data you enter into the Service — customer and supplier records, inventory, invoices, and financial records. We do not claim ownership over it. See our Privacy Policy for how that data is stored, isolated, and accessed.',
      "You're responsible for the legality and accuracy of the data you enter, and for complying with any tax, trade, or KYC/AML obligations that apply to your own business — the Service provides fields to record this information but does not verify it on your behalf.",
    ],
  },
  {
    heading: '6. Acceptable Use',
    body: [
      'You agree not to: use the Service for any unlawful purpose; attempt to access another business\'s data or bypass the access controls described in our Privacy Policy; reverse-engineer, scrape, or resell the Service without permission; or introduce malware or attempt to disrupt the Service for other users.',
    ],
  },
  {
    heading: '7. Service Availability',
    body: [
      'We aim to keep the Service available and your data backed up, but we do not guarantee uninterrupted access — maintenance, third-party infrastructure issues, or events outside our control can cause downtime. We recommend periodically exporting critical records (the Service includes CSV export on every list page) as your own additional backup.',
    ],
  },
  {
    heading: '8. Termination',
    body: [
      'You may stop using the Service at any time. We may suspend or terminate your login access to the Service for non-payment, breach of these Terms, or suspected fraudulent or unlawful use, with notice where practicable.',
      "Termination affects your access to the application only — since your business's data is stored in your own Firebase project rather than held by us, it is not deleted as a result of losing access to the Service. See our Privacy Policy for details.",
    ],
  },
  {
    heading: '9. Limitation of Liability',
    body: [
      `To the maximum extent permitted by law, ${COMPANY} is not liable for indirect, incidental, or consequential damages arising from your use of the Service, including business losses arising from data entry errors, downtime, or reliance on the Service in place of independent accounting or legal advice.`,
    ],
  },
  {
    heading: '10. Changes to These Terms',
    body: [
      'We may update these Terms from time to time. Material changes will be communicated to account owners; continued use of the Service after a change takes effect constitutes acceptance of the updated Terms.',
    ],
  },
  {
    heading: '11. Governing Law',
    body: [
      `These Terms are governed by the laws of ${JURISDICTION}, without regard to conflict-of-law principles.`,
    ],
  },
  {
    heading: '12. Contact',
    body: [
      `Questions about these Terms? Contact us at ${SUPPORT_EMAIL}.`,
    ],
  },
];

export const PRIVACY_SECTIONS: LegalSection[] = [
  {
    heading: '1. Overview',
    body: [
      `This Privacy Policy explains what information ${COMPANY} collects through this application (the "Service"), how it's used, and — most importantly for a business tool handling sensitive commercial data — how your business's data is kept separate from every other business using the Service. ${COMPANY} is based in Belgium, and this Policy is written to comply with the EU General Data Protection Regulation (GDPR).`,
    ],
  },
  {
    heading: '2. Information We Collect',
    body: [
      'Account information: your name, email, phone, and role (Owner or Staff), used to sign you in and identify who made each change within your business.',
      "Business data you enter: everything you record in the app on your business's behalf — customers, suppliers, inventory, purchase and sale invoices, payments, bank accounts and transactions, and related notes. This is your business's own operational data, and may include personal data about your own customers/suppliers (e.g. contact names) that you are responsible for having a lawful basis to process yourself.",
      'Basic usage information: sign-in times and which pages your account is granted access to, used only to operate account access controls (e.g. staff page permissions) and for security purposes such as detecting unauthorized access.',
    ],
  },
  {
    heading: '3. Legal Basis for Processing (GDPR)',
    body: [
      'We process account information and business data under the following legal bases: performance of our contract with you (providing the Service you\'ve signed up for), our legitimate interest in operating and securing the Service, and compliance with legal obligations we\'re subject to (e.g. responding to a lawful request from an authority).',
    ],
  },
  {
    heading: '4. How Your Data Is Isolated From Other Businesses',
    body: [
      "Each business on the Service is set up with its own separate Google Firebase project — a dedicated, independent cloud database that belongs to that business alone. Your data is never combined, shared, or comingled with another business's data, because there is no shared database for it to live in: every business's records live in a completely separate cloud project from every other business's, not just a separate table or partition within one shared system.",
      'Within your business, Staff accounts only see the pages the Owner has explicitly granted them access to (managed from Settings › Team & Access) — Staff cannot grant themselves additional access.',
    ],
  },
  {
    heading: '5. When We Access Your Data',
    body: [
      `When your business is first set up, ${COMPANY} configures your dedicated Firebase project with the security rules and structure the Service needs to run — this requires administrative access to your project at that point, which we use only to complete setup.`,
      `Outside of that setup step, ${COMPANY} does not routinely view or use the business data you store in the Service. We access it only when you contact us for support or ask us to help troubleshoot an issue, or where we're required to by law (for example, responding to a valid legal request). We do not access your business data to build products for other customers, to sell to third parties, or for marketing purposes.`,
    ],
  },
  {
    heading: '6. Data Storage & Security',
    body: [
      "Your data is hosted on Google Cloud infrastructure (Firebase/Firestore) within your own business's dedicated project, encrypted in transit. Access to the Service requires authentication, and every read or write from the application is scoped to that project as described above.",
      "Your login and subscription records are stored in Google Cloud's European region (Belgium and the Netherlands). Each business's own database is also created in a European region when the business is set up. If a business asks for its database to be hosted outside the EU/EEA, that transfer is covered by Google's Standard Contractual Clauses.",
      'No system is perfectly secure, and we cannot guarantee absolute security, but we design the Service around per-business data isolation as the core protection for your information.',
    ],
  },
  {
    heading: '7. Sharing With Third Parties',
    body: [
      "We do not sell your data. We share it only with the infrastructure providers necessary to run the Service (currently Google Cloud/Firebase, which stores and serves your business's database) and, where applicable, payment processors for your subscription billing — never with other businesses using the Service, and never for their marketing purposes.",
    ],
  },
  {
    heading: '8. Data Retention & Deletion',
    body: [
      "Because your business's data lives in your own Firebase project rather than a database we hold on your behalf, you control it directly — you can export or delete it at any time through your own Google account, independent of your subscription status with " + COMPANY + '.',
      'Closing your account or ending your subscription stops your access to the application itself; it does not, by itself, delete your underlying data, since that data was never ours to delete — it stays under your own control in your own Firebase project unless and until you remove it yourself.',
      `${COMPANY}'s own setup-time administrative access to your project (see "When We Access Your Data" above) is revoked when your account closes. If you'd like help exporting your data or winding down your Firebase project when you close your account, contact us at ${SUPPORT_EMAIL}.`,
    ],
  },
  {
    heading: '9. Your Rights Under GDPR',
    body: [
      'As a data subject under GDPR, you have the right to: access the personal data we hold about you; request correction of inaccurate data; request erasure ("right to be forgotten"); request a machine-readable export of your data (data portability); restrict or object to certain processing; and withdraw consent where processing is based on consent.',
      "You can access, correct, export, or delete most of your business data directly within the app (every list page includes CSV export), and — since that data lives in your own Firebase project — you can erase it entirely at any time through your own Google account, independent of us.",
      `Regarding your account with ${COMPANY} specifically: we control your login access to the application (tied to your subscription status), not the underlying data itself. If you'd like our help with an export, a correction you can't make yourself, or closing your account access, contact ${SUPPORT_EMAIL} and we will respond within the timeframe required by law.`,
    ],
  },
  {
    heading: '10. Cookies & Local Storage',
    body: [
      "The Service uses your browser's local storage to remember personal, per-device preferences — things like which table columns you've chosen to show, your light/dark theme choice, and your last-used page size. This stays on your own device, is never sent to us, and isn't used for tracking or advertising.",
    ],
  },
  {
    heading: "11. Children's Privacy",
    body: [
      'The Service is a business tool intended for use by adults operating a business. It is not directed at, and we do not knowingly collect information from, children.',
    ],
  },
  {
    heading: '12. Changes to This Policy',
    body: [
      'We may update this Privacy Policy from time to time. Material changes will be communicated to account owners before they take effect.',
    ],
  },
  {
    heading: '13. Contact Us & Supervisory Authority',
    body: [
      `Questions about this Privacy Policy or how your data is handled? Contact us at ${SUPPORT_EMAIL}.`,
      'If you believe your data protection rights have not been respected, you also have the right to lodge a complaint with the Belgian Data Protection Authority (Autorité de protection des données / Gegevensbeschermingsautoriteit), the supervisory authority for Belgium.',
    ],
  },
];
